// Face APIs handled by the BACKEND (no direct AWS calls from the browser).
//
// Endpoints return text/plain in key=value form, e.g.:
//   res=00
//   message=Register face successful
// Success convention: res=00 (register) or res=200 (pittouch authen). Both are OK.
import api from "./api";

// "res=00\nmessage=..." -> { res: "00", message: "..." }
function parseKeyValue(body: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of body.split(/\r?\n/)) {
    const idx = line.indexOf("=");
    if (idx === -1) continue;
    out[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
  }
  return out;
}

function isSuccess(kv: Record<string, string>): boolean {
  return kv.res === "00" || kv.res === "200";
}

// POST without forcing Content-Type: FormData -> multipart (browser sets the boundary),
// URLSearchParams -> application/x-www-form-urlencoded. Keep the response body as text.
async function postForm(
  path: string,
  body: FormData | URLSearchParams
): Promise<Record<string, string>> {
  try {
    const res = await api.post(path, body, {
      responseType: "text",
      transformResponse: (d) => d,
    });
    const raw = typeof res.data === "string" ? res.data : String(res.data ?? "");
    return parseKeyValue(raw);
  } catch (err: unknown) {
    // 503 SERVICE_BUSY (and other face errors) return text/plain key=value; the
    // axios interceptor rejects with response.data (string) — parse it so the
    // "アクセスが集中" message surfaces instead of a generic failure.
    const data = err as string | { error?: { message?: string }; message?: string };
    if (typeof data === "string") {
      const kv = parseKeyValue(data);
      if (kv.message || kv.error) {
        throw Object.assign(new Error(kv.message || kv.error), { error: kv });
      }
    } else if (data && typeof data === "object") {
      const msg = data.error?.message || data.message;
      if (msg) throw Object.assign(new Error(msg), { error: data.error || data });
    }
    throw err;
  }
}

// 2.2.1 Register a face (first-time). POST /v1/face-recognition/register, multipart with
// just the captured image + the challenge session_id (cognito_session_id from login). The
// backend derives the user/org from the session, then dedup -> index -> write DynamoDB.
// Responds text/plain (res=00 on success). `image` is the blob from canvas.toBlob().
export async function registerFace(image: Blob, sessionId: string): Promise<string | undefined> {
  const form = new FormData();
  form.append("files", image, "face.jpg");
  form.append("session_id", sessionId);
  const kv = await postForm("/v1/face-recognition/register", form);
  if (!isSuccess(kv)) throw new Error(kv.message || `Register failed (res=${kv.res})`);
  // The backend may rotate the session to continue the FACE_MFA verify step (checkin).
  return kv.session_id || kv.cognito_session_id || kv.session || undefined;
}

// Required after a successful check-in: finalize attendance on Salesforce (TeamSpirit).
export async function pittouchAuthen(orgId: string, faceId: string): Promise<void> {
  const body = new URLSearchParams({ cid: faceId });
  const path = `/pittouch/authen?device_type=qr&org_id=${encodeURIComponent(orgId)}`;
  const kv = await postForm(path, body);
  if (!isSuccess(kv)) throw new Error(kv.message || `Authen failed (res=${kv.res})`);
}
